import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GameLayout from '../layout/GameLayout';
import FloatingCard from '../ui/FloatingCard';
import FloatingButton from '../ui/FloatingButton';
import EntityCard from '../ui/EntityCard';
import dropInService from '../../services/api/dropInService';
import { eid } from '../../utils/entityId';
import { getUserSports, getAllSports } from '../../services/api/sportsService';
import FilterBar from '../ui/FilterBar';
import { useUserFilters } from '../../contexts/UserFiltersContext';
import { mergeMyDropIns, mineFirst } from '../../utils/listOrder';
import StatusTabs from '../ui/StatusTabs';
import '../../styles/features/dropIn.css';
import { eventTimeZone, formatEventTimeForViewer } from '../../utils/eventTime';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
];

const SKILL_EMOJI = {
  recreational: '🟢',
  intermediate:  '🟡',
  competitive:   '🔴',
};

/** At the venue's local time (utils/eventTime) */
const fmtDate = (iso, timeZone) => {
  if (!iso) return '—';
  return formatEventTimeForViewer(iso, timeZone, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const CapacityBar = ({ confirmed, checkedIn, max }) => {
  const used = confirmed + checkedIn;
  const pct  = Math.min(100, (used / Math.max(max, 1)) * 100);
  return (
    <div className="dropin-card__capacity">
      <span>{used}/{max}</span>
      <div className="dropin-capacity-bar">
        <div
          className={`dropin-capacity-bar__fill${pct >= 100 ? ' dropin-capacity-bar__fill--full' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span>{max - used > 0 ? `${max - used} left` : 'Full'}</span>
    </div>
  );
};

const DropInList = ({ token, user, onLogout }) => {
  const navigate = useNavigate();
  const { filterParams, autoExpandRadius, isFiltersReady, needsLocation } = useUserFilters();
  const [dropIns, setDropIns]   = useState([]);
  const [sports, setSports]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [filters, setFilters]   = useState({
    status:      '',
    sport_id:    '',
    skill_level: '',
  });
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'mine'
  // Sessions I host or joined: shown first in every list, marked
  const [mine, setMine] = useState({ hosted: new Set(), joined: new Set() });
  // Only the latest load may fill the list (switching tabs mid-load)
  const loadSeq = useRef(0);

  const loadDropIns = useCallback(async () => {
    if (!isFiltersReady && activeTab !== 'mine') return;
    // No location yet: the FilterBar asks for one; never list every region
    if (needsLocation && activeTab !== 'mine') {
      setDropIns([]);
      setLoading(false);
      return;
    }
    const seq = ++loadSeq.current;
    const latest = () => seq === loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'mine' && user) {
        const res = await dropInService.myDropIns();
        if (!latest()) return;
        const { hosted = [], joined = [] } = res.data || {};
        setDropIns(mergeMyDropIns(hosted, joined));
      } else {
        const params = {};
        if (filters.status)      params.status      = filters.status;
        // All Statuses lists finished sessions too, after the live and upcoming ones
        if (!filters.status)     params.include_past = true;
        if (filters.sport_id)    params.sport_id    = filters.sport_id;
        if (filters.skill_level) params.skill_level = filters.skill_level;
        // Merge global sport + location filters.
        // If a specific sport is selected locally, drop the context's sport_ids
        // so it doesn't block the selected sport.
        const effectiveFilterParams = filters.sport_id
          ? { ...filterParams, sport_ids: undefined }
          : filterParams;
        const globalParams = { ...effectiveFilterParams, ...params };
        const [res, my] = await Promise.all([
          dropInService.list(globalParams),
          // Only for the order and badges: a failure never blocks the list
          user ? Promise.resolve().then(() => dropInService.myDropIns()).catch(() => null) : null,
        ]);
        if (!latest()) return;
        const { hosted = [], joined = [] } = my?.data || {};
        const own = { hosted: new Set(hosted.map((d) => d.id)), joined: new Set(joined.map((d) => d.id)) };
        setMine(own);
        const items = res.data?.dropIns || [];
        setDropIns(mineFirst(items, new Set([...own.hosted, ...own.joined])));
        if (filterParams.lat != null) autoExpandRadius(items.length);
      }
    } catch (err) {
      if (latest()) setError(err.message || 'Failed to load drop-in sessions');
    } finally {
      if (latest()) setLoading(false);
    }
  }, [activeTab, filters, user, filterParams, isFiltersReady, needsLocation]);

  useEffect(() => {
    const loadSports = async () => {
      try {
        const userRes = await getUserSports();
        const data = userRes?.data?.sports || userRes?.sports || userRes?.data || [];
        if (Array.isArray(data) && data.length) { setSports(data); return; }
      } catch { /* fall through */ }
      getAllSports()
        .then((res) => setSports(res.data || []))
        .catch(() => {});
    };
    loadSports();
  }, []);

  useEffect(() => { loadDropIns(); }, [loadDropIns]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ status: '', sport_id: '', skill_level: '' });
  };

  const hasActiveFilters = filters.status || filters.sport_id || filters.skill_level;

  return (
    <GameLayout token={token} onLogout={onLogout}>
      <div className="dropin-page">

        {/* Header — same compact style as teams/matches list pages */}
        <FloatingCard variant="primary" className="dropin-list-header-card">
          <div className="header-content-gaming">
            <div className="title-section-gaming">
              <h1 className="dropin-page-title">🏃 Drop-in Sessions</h1>
              <p className="dropin-page-subtitle">Open-play events — join, play, and meet new people</p>
            </div>
            <div className="header-actions-gaming">
              <FloatingButton
                variant="secondary"
                size="medium"
                onClick={() => navigate('/dashboard')}
                icon="←"
              >
                Back
              </FloatingButton>
              {user && (
                <FloatingButton
                  variant="success"
                  size="medium"
                  onClick={() => navigate('/drop-in/create')}
                  icon="+"
                >
                  Create Drop-in
                </FloatingButton>
              )}
            </div>
          </div>
        </FloatingCard>

        {/* Tabs + Filters wrapped in a floating card */}
        <FloatingCard variant="outline" className="dropin-filters-card">
          <FilterBar inline showSports={false} />
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: activeTab === 'all' ? '0.75rem' : 0 }}>
            {['all', 'mine'].map((tab) => (
              <button
                key={tab}
                className={`dropin-btn ${activeTab === tab ? 'dropin-btn--primary' : 'dropin-btn--secondary'}`}
                style={{ width: 'auto', padding: '0.4rem 1rem', fontSize: '0.85rem' }}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'all' ? '🌐 All Sessions' : '⭐ My Sessions'}
              </button>
            ))}
          </div>

          {/* Filters (only for All tab) */}
          {activeTab === 'all' && (
            <StatusTabs
              tabs={STATUS_TABS}
              value={filters.status}
              onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
              label="Drop-in status"
            />
          )}
          {activeTab === 'all' && (
            <div className="dropin-filters" style={{ marginBottom: 0 }}>
              <select name="sport_id" value={filters.sport_id} onChange={handleFilterChange} className="dropin-filter-select">
                <option value="">All Sports</option>
                {sports.map((s) => <option key={s.sport_id || s.id} value={s.sport_id || s.id}>{s.name}</option>)}
              </select>
              <select name="skill_level" value={filters.skill_level} onChange={handleFilterChange} className="dropin-filter-select">
                <option value="">All Levels</option>
                <option value="recreational">🟢 Recreational</option>
                <option value="intermediate">🟡 Intermediate</option>
                <option value="competitive">🔴 Competitive</option>
              </select>
              {hasActiveFilters && (
                <FloatingButton
                  variant="warning"
                  size="small"
                  onClick={clearFilters}
                  icon="🧹"
                >
                  <span className="btn-text">Clear Filters</span>
                </FloatingButton>
              )}
            </div>
          )}
        </FloatingCard>

        {/* Content */}
        {loading && <div className="dropin-loading">⏳ Loading sessions…</div>}
        {error   && <div className="dropin-empty"><div className="dropin-empty__icon">⚠️</div><div className="dropin-empty__title">{error}</div></div>}

        {!loading && !error && dropIns.length === 0 && (
          <div className="dropin-empty">
            <div className="dropin-empty__icon">🏐</div>
            <div className="dropin-empty__title">
              {needsLocation && activeTab !== 'mine' ? 'Drop-ins near you' : 'No drop-in sessions found'}
            </div>
            <div className="dropin-empty__sub">
              {activeTab === 'mine'
                ? 'You haven\'t created or joined any drop-ins yet.'
                : needsLocation
                ? "Set your location above to see what's near you."
                : 'Be the first to create one in your area!'}
            </div>
            {user && !(needsLocation && activeTab !== 'mine') && (
              <button
                className="dropin-btn dropin-btn--primary"
                style={{ width: 'auto', padding: '0.6rem 1.5rem', margin: '1rem auto 0' }}
                onClick={() => navigate('/drop-in/create')}
              >
                + Create Drop-in
              </button>
            )}
          </div>
        )}

        {!loading && !error && dropIns.length > 0 && (
          <div className="dropin-grid">
            {dropIns.map((di) => {
              const d = di.toJSON ? di.toJSON() : di;
              const confirmed   = d.confirmedCount  || d.confirmed_count  || 0;
              const checkedIn   = d.checkedInCount  || d.checked_in_count || 0;
              const waitlisted  = d.waitlistedCount || d.waitlisted_count || 0;
              const max         = d.maxPlayers      || d.max_players      || 12;
              const skillLevel  = d.skillLevel || d.skill_level;
              const venue       = [d.venue || d.venueAddress, d.court].filter(Boolean).join(' · ');
              const open        = () => navigate(`/drop-in/${eid(d)}`);
              return (
                <EntityCard
                  key={d.id}
                  className="dropin-list-card"
                  title={d.title}
                  badges={[
                    { status: d.status },
                    { label: d.sportName || d.sport_name || '—', tone: 'sport' },
                    skillLevel && { label: `${SKILL_EMOJI[skillLevel] || ''} ${skillLevel}`.trim(), tone: 'neutral' },
                    d.visibility === 'private' && { label: '🔒 Private', tone: 'neutral' },
                    mine.hosted.has(d.id) ? { label: '⭐ Hosting', tone: 'success' }
                      : mine.joined.has(d.id) && { label: '⭐ Joined', tone: 'success' },
                  ].filter(Boolean)}
                  rows={[
                    { key: 'when', icon: '📅', label: 'When', value: fmtDate(d.scheduledAt || d.scheduled_at, eventTimeZone(d)) },
                    venue && { key: 'venue', icon: '📍', label: 'Venue', value: venue },
                    {
                      key: 'spots',
                      icon: '👥',
                      label: 'Spots',
                      value: (
                        <>
                          <CapacityBar confirmed={confirmed} checkedIn={checkedIn} max={max} />
                          {waitlisted > 0 && <span className="dropin-card__waitlist">⏳ {waitlisted} on waitlist</span>}
                        </>
                      ),
                    },
                    { key: 'host', icon: '🏆', label: 'Host', value: d.hostName || d.host_name || 'Unknown' },
                  ].filter(Boolean)}
                  onClick={open}
                />
              );
            })}
          </div>
        )}
      </div>
    </GameLayout>
  );
};

export default DropInList;
